import type { ActionBarRow } from '../../player-character-sheet/types';
import type { AppliedFrame, AppliedRow, AppliedRowIdentity, AppliedTouchedRow } from './applied-frame';
import { readItemField } from '../compile/item-field-reader';
import { constructPlayerActionBarFromBase } from '../compile/from-base-player-display';

const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const field = (row: AppliedRow, key: string): string => upper(readItemField(row, key));

/** Native saved rows only; an unchanged bar/slot contributes no delta. */
export function actionBarSavedRows(owner: string, before: readonly ActionBarRow[], after: readonly ActionBarRow[]) {
    const flatten = (bars: readonly ActionBarRow[]) => bars.flatMap(bar => {
        const { slots = [], ...columns } = bar;
        return [{ table: 'action_bars', row_id: String(bar.bar_id), row: { ...columns, owner_id: owner, owner_type: 'player_character', label: bar.label ?? null } },
            ...slots.map(slot => ({ table: 'action_bar_slots', row_id: String(slot.slot_id),
                row: { ...slot, bar_id: bar.bar_id, hot_key: slot.hot_key ?? null, source_id: slot.source_id ?? null } }))];
    });
    const old = new Map(flatten(before).filter(row => row.row_id !== 'undefined').map(row => [`${row.table}:${row.row_id}`, row]));
    const next = flatten(after), rows: AppliedTouchedRow[] = [];
    for (const row of next) {
        const key = `${row.table}:${row.row_id}`, prior = old.get(key)?.row;
        if (!prior || Object.keys(row.row).some(key => readItemField(prior, key) !== readItemField(row.row, key))) rows.push(row);
        old.delete(key);
    }
    // Carry the native parent as a routing witness for slot-only and delete-only saves.
    const touchedBars = new Set([...rows, ...old.values()].map(row => row.table === 'action_bars'
        ? upper(row.row_id) : field(row.row, 'bar_id')));
    for (const bar of [...flatten(after), ...old.values()].filter(row => row.table === 'action_bars'))
        if (touchedBars.has(upper(bar.row_id)) && !rows.some(row => row.table === 'action_bars' && row.row_id === bar.row_id)) rows.push(bar);
    return { rows, deletes: [...old.values()].map(({ table, row_id }) => ({ table, row_id })) };
}

/** Patch only the affected saved rows and displayed bars; other clusters keep their identity. */
export function applySavedActionBars(source: AppliedRow, frame: Pick<AppliedFrame, 'rows' | 'deletes'>, raw: boolean): AppliedRow | null {
    const pc = source.player_characters;
    if (!pc || typeof pc !== 'object' || Array.isArray(pc)) return null;
    const owner = field(pc as AppliedRow, 'item_id');
    // Preferences replace an owner's small value set. SQL row ids are not part of
    // this transport: preserve held identities where available, never invent them.
    const preferences: AppliedRow = {};
    for (const saved of frame.rows.filter(row => field(row.row, 'owner_id') === owner)) {
        if (saved.table === 'action_bar_dividers') {
            const positions = readItemField({ positions: [saved.row.positions] }, 'positions') as number[];
            const held = (raw && Array.isArray(source.action_bar_dividers) ? source.action_bar_dividers : []) as AppliedRow[];
            preferences.action_bar_dividers = raw ? positions.map(position => held.find(row => Number(readItemField(row, 'position')) === position)
                ?? { owner_id: owner, owner_type: 'player_character', position }) : [...positions];
        }
        if (saved.table === 'action_bar_settings') {
            const settings = readItemField(saved.row, 'settings') as AppliedRow;
            if (raw) {
                const held = (Array.isArray(source.action_bar_settings) ? source.action_bar_settings : []) as AppliedRow[];
                preferences.action_bar_settings = [...held.filter(row => !(String(readItemField(row, 'action_bar_setting_key')) in settings)),
                    ...Object.entries(settings).map(([key, value]) => ({
                        ...held.find(row => readItemField(row, 'action_bar_setting_key') === key),
                        action_bar_setting_owner_id: owner, action_bar_setting_owner_type: 'player_character',
                        action_bar_setting_key: key, action_bar_setting_value: JSON.stringify(value),
                    }))];
            } else preferences.action_bar_settings = { ...(source.action_bar_settings as AppliedRow ?? {}), ...settings };
        }
    }
    const bars = (Array.isArray(source.action_bars) ? source.action_bars : []) as AppliedRow[];
    const barIds = new Set(bars.map(bar => field(bar, 'bar_id')));
    const incoming = frame.rows.filter(row => row.table === 'action_bars' && field(row.row, 'owner_id') === owner);
    incoming.forEach(row => barIds.add(upper(row.row_id)));
    const slots = frame.rows.filter(row => row.table === 'action_bar_slots' && barIds.has(field(row.row, 'bar_id')));
    const deleted = (table: string, id: string): boolean => frame.deletes.some(row => row.table === table && upper(row.row_id) === id);
    const affected = new Set(incoming.map(row => upper(row.row_id)));
    for (const row of slots) affected.add(field(row.row, 'bar_id'));
    for (const bar of bars) if (deleted('action_bars', field(bar, 'bar_id'))
        || !raw && (bar.slots as AppliedRow[] | undefined)?.some(slot => deleted('action_bar_slots', field(slot, 'slot_id')))) affected.add(field(bar, 'bar_id'));
    const savedSlots = raw ? (Array.isArray(source.action_bar_slots) ? source.action_bar_slots : []) as AppliedRow[]
        : bars.flatMap(bar => ((bar.slots as AppliedRow[] | undefined) ?? []).map(slot => ({ ...slot, bar_id: readItemField(bar, 'bar_id') })));
    for (const slot of savedSlots) if (deleted('action_bar_slots', field(slot, 'slot_id'))) affected.add(field(slot, 'bar_id'));
    if (!affected.size) return Object.keys(preferences).length ? preferences : null;
    const merge = (held: AppliedRow[], incoming: readonly AppliedTouchedRow[], table: string, idKey: string): AppliedRow[] => {
        const rows = new Map(held.filter(row => !deleted(table, field(row, idKey))).map(row => [field(row, idKey), row]));
        for (const row of incoming) if (!deleted(table, upper(row.row_id))) rows.set(upper(row.row_id), { ...row.row });
        return [...rows.values()];
    };
    const nextSlots = merge(savedSlots, slots, 'action_bar_slots', 'slot_id').filter(slot => !deleted('action_bars', field(slot, 'bar_id')));
    const nextBars = merge(bars, incoming, 'action_bars', 'bar_id').sort((a, b) => Number(readItemField(a, 'bar_index')) - Number(readItemField(b, 'bar_index')));
    return raw ? { ...preferences, action_bars: nextBars, action_bar_slots: nextSlots }
        : { ...preferences, action_bars: nextBars.map(bar => affected.has(field(bar, 'bar_id')) ? constructPlayerActionBarFromBase(bar, nextSlots) : bar) };
}

/** Layout-only frames never visit inventory, statuses or computed clusters. */
export function actionBarsOnlyFrame(frame: AppliedFrame, allowEmpty = false): boolean {
    return (allowEmpty || [...frame.rows, ...frame.deletes].length > 0)
        && [...frame.rows, ...frame.deletes].every(row => ['action_bars', 'action_bar_slots', 'action_bar_dividers', 'action_bar_settings'].includes(row.table))
        && !Object.keys(frame.member_revs).length
        && !Object.keys(frame.computed).length && !Object.keys(frame.computed_delta ?? {}).length
        && !frame.new_entities.length && !frame.new_inventory?.length && !frame.inputs;
}

export function patchSavedActionBarTree(source: unknown, frame: AppliedFrame, raw: boolean): unknown {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return source;
    const row = source as AppliedRow, layout = applySavedActionBars(row, frame, raw);
    let next = layout ? { ...row, ...layout } : row;
    if (Array.isArray(row.encounter_creatures_arr)) {
        let changed = false;
        const members = row.encounter_creatures_arr.map(member => {
            const patched = patchSavedActionBarTree(member, frame, raw);
            changed ||= patched !== member;
            return patched;
        });
        if (changed) next = { ...next, encounter_creatures_arr: members };
    }
    return next;
}

/** PC preferences are shared by its standalone facet and every encounter membership. */
export function holdsSavedActionBars(source: unknown, frame: AppliedFrame): boolean {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return false;
    const row = source as AppliedRow, pc = row.player_characters;
    if (pc && typeof pc === 'object' && !Array.isArray(pc) && frame.rows.some(saved => ['action_bars', 'action_bar_dividers', 'action_bar_settings'].includes(saved.table)
        && field(saved.row, 'owner_id') === field(pc as AppliedRow, 'item_id'))) return true;
    return Array.isArray(row.encounter_creatures_arr) && row.encounter_creatures_arr.some(member => holdsSavedActionBars(member, frame));
}

export interface SavedRowCorrection { deletes?: readonly AppliedRowIdentity[]; operationId?: string }

declare global { interface Window {
    ftCorrectSavedRows?: (id: string, rows: readonly AppliedTouchedRow[], correction?: SavedRowCorrection) => void;
} }
